import { useLiveQuery } from '@electric-sql/pglite-react';
import type { ReportContent } from '@quickreport/domain';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { SectionTabs } from './SectionTabs';

interface Row {
  id: string;
  name: string;
  lab_no: string;
  content: ReportContent;
}

/** The results entered so far, out of the tests that need one (calculated ones are not typed). */
function progress(content: ReportContent): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const s of content.sections) {
    for (const r of s.doc.rows) {
      if (r.kind !== 'test' || r.formula) continue;
      total++;
      if ((s.values[r.id] ?? '').trim() !== '') done++;
    }
  }
  return { done, total };
}

function titleOf(r: { name: string; content: ReportContent }): string {
  const first = r.content.sections[0]?.doc.name;
  if (r.content.kind === 'generic' && r.content.title) return r.content.title;
  return first ?? r.name;
}

/**
 * Every report of this patient's visit as a row of tabs. Each chosen test is its own report, so
 * this is how a person moves from one test to the next to write its values. Shown only when the
 * visit has more than one report. What is typed is saved before the next report opens.
 */
export function VisitReports({
  visitId,
  currentId,
  liveContent,
  beforeLeave,
}: {
  visitId: string;
  currentId: string;
  /** The open report as it is on screen, so its progress is not a save behind. */
  liveContent: ReportContent;
  beforeLeave: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const rows = useLiveQuery<Row>(
    `select id, name, lab_no, content from reports
      where visit_id = $1 and deleted_at is null order by lab_no, created_at`,
    [visitId],
  );
  const list = useMemo(() => rows?.rows ?? [], [rows]);
  if (list.length < 2) return null;

  const active = Math.max(
    0,
    list.findIndex((r) => r.id === currentId),
  );
  return (
    <div className="mt-4" aria-label="Reports of this visit">
      <div className="mb-1 text-[12px] font-medium text-slate">
        This visit has {list.length} reports. Choose one to write its results.
      </div>
      <SectionTabs
        active={active}
        onChange={async (i) => {
          const to = list[i];
          if (!to || to.id === currentId) return;
          await beforeLeave();
          void navigate({ to: '/reports/$reportId', params: { reportId: to.id } });
        }}
        tabs={list.map((r) => {
          const p = progress(r.id === currentId ? liveContent : r.content);
          return {
            key: r.id,
            label: titleOf(r),
            total: p.total,
            done: p.done,
            flagged: 0,
          };
        })}
      />
    </div>
  );
}
